import React from 'react';
import {
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  UserIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  CurrencyPoundIcon,
  DocumentCheckIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatTimeAgo, NotificationTypes } from '../../utils/notifications';
import './NotificationTray.css';

interface NotificationTrayProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationTray: React.FC<NotificationTrayProps> = ({ isOpen, onClose }) => {
  const { 
    notifications, 
    loading, 
    error, 
    markAsRead, 
    markAllAsRead, 
    handleAction 
  } = useNotifications();

  if (!isOpen) return null;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case NotificationTypes.JOB_ASSIGNED:
      case NotificationTypes.JOB_STARTED:
      case NotificationTypes.JOB_COMPLETED:
      case NotificationTypes.JOB_CANCELLED:
        return <BriefcaseIcon className="w-5 h-5 text-blue-500" />;

      case NotificationTypes.CERTIFICATE_ISSUED:
      case NotificationTypes.CERTIFICATE_SUBMITTED:
        return <DocumentCheckIcon className="w-5 h-5 text-teal-500" />;
      case NotificationTypes.CERTIFICATE_EXPIRY:
        return <ShieldCheckIcon className="w-5 h-5 text-orange-500" />;

      case NotificationTypes.INVOICE_PAID:
        return <CurrencyPoundIcon className="w-5 h-5 text-emerald-500" />;
      case NotificationTypes.INVOICE_SENT:
      case NotificationTypes.INVOICE_VIEWED:
        return <DocumentTextIcon className="w-5 h-5 text-blue-500" />;
      case NotificationTypes.INVOICE_OVERDUE:
        return <DocumentTextIcon className="w-5 h-5 text-red-500" />;

      case NotificationTypes.QUOTE_SENT:
      case NotificationTypes.QUOTE_ACCEPTED:
        return <DocumentCheckIcon className="w-5 h-5 text-emerald-500" />;
      case NotificationTypes.QUOTE_REJECTED:
        return <DocumentCheckIcon className="w-5 h-5 text-red-500" />;
      case NotificationTypes.QUOTE_CHANGES_REQUESTED:
        return <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-amber-500" />;

      case NotificationTypes.TEAM_INVITE_ACCEPTED:
      case NotificationTypes.CLIENT_PORTAL_JOINED:
      case NotificationTypes.FOLLOW:
      case NotificationTypes.INVITE:
        return <UserIcon className="w-5 h-5 text-purple-500" />;

      case NotificationTypes.COMMENT:
        return <ChatBubbleLeftEllipsisIcon className="w-5 h-5 text-blue-500" />;
      case NotificationTypes.SHARE:
        return <DocumentTextIcon className="w-5 h-5 text-gray-500" />;

      case NotificationTypes.SYSTEM_ALERT:
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
  };

  const handleActionClick = async (notificationId: string, action: 'accept' | 'decline', e: React.MouseEvent) => {
    e.stopPropagation();
    await handleAction(notificationId, action);
  };

  return (
    <div className="notification-tray">
      <div className="notification-header">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Notifications</h3>
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            <CheckIcon className="w-4 h-4" />
            Mark all as read
          </button>
        </div>
      </div>

      {/* Section Header */}
      <div className="px-6 py-3 bg-gray-50">
        <h4 className="text-sm font-medium text-gray-500">Today</h4>
      </div>

      <div className="notification-list max-h-[500px] overflow-y-auto">
        {loading && notifications.length === 0 ? (
          <div className="px-6 py-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
              <span className="ml-2 text-gray-500">Loading notifications...</span>
            </div>
          </div>
        ) : error && notifications.length === 0 ? (
          <div className="px-6 py-8">
            <div className="flex items-center justify-center text-red-500">
              <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
              <span>Failed to load notifications</span>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-6 py-8">
            <div className="flex items-center justify-center text-gray-500">
              <span>No notifications yet</span>
            </div>
          </div>
        ) : (
          notifications.map((notification, index) => {
            const bgColor = !notification.read 
              ? index % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'
              : 'bg-white';
            
            return (
              <div
                key={notification.id}
                className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${bgColor} border-b border-gray-100 last:border-b-0`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal-600"></span>
                          <h4 className="text-sm font-semibold text-gray-900">{notification.title}</h4>
                        </div>
                        <p className="mt-1 text-sm text-gray-600 leading-relaxed">{notification.message}</p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                    
                    {notification.actionRequired && notification.actionData && (
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          onClick={(e) => handleActionClick(notification.id, 'accept', e)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                        >
                          <CheckIcon className="w-3.5 h-3.5 mr-1" />
                          Accept
                        </button>
                        <button
                          onClick={(e) => handleActionClick(notification.id, 'decline', e)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <XMarkIcon className="w-3.5 h-3.5 mr-1" />
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200">
        <button className="text-sm font-medium text-gray-900 hover:text-gray-700">
          View all notifications
        </button>
      </div>
    </div>
  );
};

export default NotificationTray;
